import { HttpErrorResponse } from '@angular/common/http';
import { differenceInYears, differenceInDays, differenceInMonths } from 'date-fns';
import { ParecerIndicativo } from 'src/app/model/indicator';
import addPeriods from './addPeriods';

export const isNullOrUndefined = (object: any): boolean => {
	return object === null || object === undefined;
};

export const isNotNullAndUndefined = (object: any): boolean => {
	return object !== null && object !== undefined;
};

export const isNumber = (object: any): boolean => {
	return typeof object === 'number';
};

export const isDate = (object: any): boolean => {
	return object instanceof Date;
};

export const isObject = (object: any): boolean => {
	return object instanceof Object;
};

export const isArray = (object: any): boolean => {
	return object instanceof Array;
};

export const round = (number: number, precision: number = 2): number => {
	const factor = Math.pow(10, precision);
	return Math.round(number * factor) / factor;
};

export const calculatePercent = (value: number, other: number, precision: number = 4): number => {
	if ((value || 0) === 0) {
		return 0;
	} else if ((other || 0) === 0) {
		return 100;
	} else {
		return round(value / (other / 100), precision);
	}
};

export const calculateDifferenceInYears = (dateStart: Date | number, dateEnd: Date | number): number => {
	return differenceInYears(dateEnd, dateStart);
};

export const calculateDifferenceInMonths = (dateStart: Date | number, dateEnd: Date | number): number => {
	return differenceInMonths(dateEnd, dateStart);
};

export const calculateDifferenceInDays = (dateStart: Date | number, dateEnd: Date | number): number => {
	return differenceInDays(dateEnd, dateStart);
};

export const calculateDiffInYearsAndMonths = (days: number): string => {
	const years = Math.floor(days / 365);
	const yearsStr = (years > 0 ? (years.toString() + 'ano') + (years > 1 ? 's' : '') : '');
	const months = Math.floor((days % 365) / 30);
	const monthsStr = (months > 0 ? months.toString() + (months > 1 ? 'meses' : 'mês') : '');

	return (yearsStr.length > 0 ? yearsStr : '') +
		(yearsStr.length > 0 && monthsStr.length > 0 ? ' e ' : '') +
		(monthsStr.length > 0 ? monthsStr : '');
};

export const calculateDiffInYearsAndMonths2 = (days: number): string => {
	const years = Math.floor(days / 365);
	const yearsStr = (years > 0 ? (years.toString() + 'a.') : '');
	const months = Math.floor((days % 365) / 30);
	const monthsStr = (months > 0 ? months.toString() + 'm.' : '');

	return `${yearsStr}${monthsStr}`;
};

// nossa função delay com suporte a promisse.
export const delay = (ms: number): Promise<boolean> => {
	return new Promise(resolve => {
		setTimeout(() => {
			resolve(true);
		}, ms);
	});
};

export const getErrorMessageHttp = (error: HttpErrorResponse): string => {
	return error.error && error.error.message ? error.error.message : error.message || error.toString();
};

export const sortList = <T>(list: Array<T>, fieldsSort: Array<string>): Array<T> => {
	const fieldSorter = (fields: Array<string>) => (a: T, b: T) => fields.map(o => {
		let dir = 1;
		if (o[0] === '-') {
			dir = -1;
			o = o.substring(1);
		}
		return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
	}).reduce((p, n) => p ? p : n, 0);
	return list.sort(fieldSorter(fieldsSort));
};

export const leftPad = (value: string, length: number, char = ' '): string => {
	const str = String(value);
	const len = length - str.length + 1;
	return len > 0 ?
		new Array(len).join(char) + str : str;
};

export const rightPad = (value: string, length: number, char = ' '): string => {
	const str = String(value);
	const len = length - str.length + 1;
	return len > 0 ?
		str + new Array(len).join(char) : str;
};

export const returnAcceptanceProps = (configAcceptance: Array<ParecerIndicativo>, acceptance: number): ParecerIndicativo => {
	return configAcceptance.find((c) => acceptance >= c.valueStart && acceptance <= c.valueEnd) || {} as ParecerIndicativo;
};

export const diferencaDatas = (dataInicial: number, dataFinal: number): string => {
	const diffMs = (dataFinal - dataInicial);
	const diffHrs = Math.floor((diffMs % 86400000) / 3600000);
	const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);
	return `${leftPad(diffHrs.toString(), 2)}:${leftPad(diffMins.toString(), 2)}`;
};

export const diffDates = (dataInicial: number, dataFinal?: number): string => {
	const finalDate = dataFinal || new Date().getTime();
	const diffMs = finalDate - dataInicial;
	const diffDays = Math.floor(diffMs / 86400000);
	const diffHrs = Math.floor((diffMs % 86400000) / 3600000);
	const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);

	let str = '';
	if (diffDays > 0) {
		str += ` ${diffDays} ${diffDays > 1 ? 'dias' : 'dia'}`;
	}
	if (diffHrs > 0) {
		str += ` ${diffHrs} ${diffHrs > 1 ? 'horas' : 'hora'}`;
	}
	str += ` ${diffMins} ${diffMins > 1 ? 'minutos' : 'minuto'}`;
	return str;
};

export const blobToFile = (blob: Blob, fileName: string): File => {
	return new File([blob], fileName, { lastModified: new Date().getTime(), type: blob.type });
};

export const splitTwoLastWords = (text: string): string => {
	const words = (text || '').split(' ');
	if (words.length > 1) {
		return `${words[words.length - 2]} ${words[words.length - 1]}`;
	} else if (words.length > 0) {
		return words[words.length - 1];
	} else {
		return '';
	}
};

export const splitLastWords = (text: string): string => {
	const words = (text || '').split(' ');
	if (words.length > 0) {
		return words[words.length - 1];
	} else {
		return '';
	}
};

export const formatInteiro = (value: number): string => {
	return isNotNullAndUndefined(value) ? addPeriods(Number(value).toFixed(0).replace('.', ',')) : '';
};

export const formatMoeda = (value: number): string => {
	return isNotNullAndUndefined(value) ? `R$${addPeriods(Number(value).toFixed(2).replace('.', ','))}` : '';
};

export const ratingToAcceptance = (rating: string): string => {
	let result = '';
	if (rating === 'C') {
		result = '0 a 1,99';
	} else if (rating === 'C+' || rating === 'CP') {
		result = '2 a 2,99';
	} else if (rating === 'CC') {
		result = '3 a 3,99';
	} else if (rating === 'CCC') {
		result = '4 a 4,99';
	} else if (rating === 'B') {
		result = '5 a 6';
	} else if (rating === 'B+' || rating === 'BP') {
		result = '6,01 a 6,50';
	} else if (rating === 'BBB') {
		result = '6,51 a 7';
	} else if (rating === 'A') {
		result = '7,01 a 7,50';
	} else if (rating === 'A+' || rating === 'AP') {
		result = '7,51 a 8';
	} else if (rating === 'AA') {
		result = '8,01 a 9';
	} else if (rating === 'AAA') {
		result = '9,01 a 10';
	}
	return result;
};

export const validateNumericRangeParecerIndicativo = (parecerIndicativoList) => {
	// range de 0 a 1000
	// equivale a 1001 posições
	let totalRange = Array.from({ length: 1001 })

	// Verifica se algum indicativo esta se sobrepondo a outro
	for (let parecer of parecerIndicativoList) {	

		for (let i = parecer.valueStart * 100; i <= parecer.valueEnd * 100; i++) {
			if (totalRange[i] === undefined) {
				totalRange[i] = true;
			} else {
				return "Valores do parecer indicativo não deve se sobrepor!"
			}
		}
	}

	//console.table(totalRange)

	// Verifica se todo o range está completo
	for (let position of totalRange) {		
		if (position === undefined) return "Parecer indicativo deve estar completo de 0 a 10"
	}

	return null;
}

//Remove os pontos e barras de um numero de CPF ou CNPJ 
export const removeSpecialCharacters = (cpfCnpj: string): string => {
	return cpfCnpj.replace(/[^a-z0-9]/gi, '');
}

export const ajustDateStartEnd = (oldDates:number[]):number[]=>{
	let dates: Date[] = [new Date(oldDates[0]),new Date(oldDates[1])]
	if (dates && dates.length > 0 && dates[0]) {
		dates[0].setHours(0, 0, 0);
	  }
	if (dates && dates.length > 1 && dates[1]) {
		dates[1].setHours(23, 59, 59);
	}
	return [dates[0].getTime(),dates[1].getTime()]
}

